import { useCallback, useEffect, useState } from 'react';
import {
  type CaptureAddressItem,
  type CatalogoSugestao,
  type EmailConfigItem,
  type MemberRole,
  type MunicipioItem,
  type OrgSettings,
  type PendingInvite,
  type TeamMember,
  activateMember,
  addCaptureSender,
  cancelInvite,
  createCatalogoSugestao,
  createEmailConfig,
  createMunicipio,
  deactivateMember,
  deleteEmailConfig,
  deleteMunicipio,
  fetchCatalogoSugestoes,
  fetchMe,
  getCaptureAddress,
  getOrgSettings,
  inviteMember,
  listEmailConfigs,
  listMunicipios,
  listPendingInvites,
  listTeamMembers,
  regenerateCaptureAddress,
  removeCaptureSender,
  removeMember,
  resendInvite,
  resetMemberPassword,
  testEmailConfig,
  updateEmailConfig,
  updateMemberRole,
  updateMunicipio,
  updateOrgSettings,
} from '../services/api';
import { useT } from '../i18n';

// S10-T01 (US-S10-001): painel administrativo do tenant.
// Tabs: Usuarios, Provedores (NFS-e), E-mail (IMAP/SMTP), Retencao (LGPD)
// e Thresholds. Guard por role feito no App.tsx (MANAGE_ROLES).

type TabId = 'users' | 'providers' | 'email' | 'retention' | 'thresholds';

const TABS: TabId[] = ['users', 'providers', 'email', 'retention', 'thresholds'];

const ROLE_OPTIONS: MemberRole[] = [
  'admin',
  'member',
  'contador',
  'revisor',
  'financeiro',
  'operador',
  'auditor',
];

function roleLabel(role: string, t: (k: string) => string): string {
  const key = `settings.role.${role}`;
  const label = t(key);
  return label === key ? role : label;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('pt-BR');
  } catch {
    return iso;
  }
}

const inputCls =
  'w-full rounded-md border border-brand-300 px-3 py-2 text-sm text-brand-800';
const btnPrimary =
  'rounded-md bg-brand-700 px-4 py-2 text-sm font-medium text-white hover:bg-brand-800 disabled:opacity-50';
const btnDanger =
  'rounded-md border border-red-300 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50';
const btnNeutral =
  'rounded-md border border-brand-300 px-2 py-1 text-xs font-medium text-brand-700 hover:bg-brand-50';

// ---------------------------------------------------------------------------
// Tab: Usuarios
// ---------------------------------------------------------------------------

function UsersTab() {
  const t = useT();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [invites, setInvites] = useState<PendingInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<MemberRole>('member');
  const [inviteBusy, setInviteBusy] = useState(false);

  const [resetResult, setResetResult] = useState<{ email: string; password: string } | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [m, i] = await Promise.all([listTeamMembers(), listPendingInvites()]);
      setMembers(m.members ?? []);
      setInvites(i.invites ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('settings.error.load'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const run = useCallback(
    async (id: string, fn: () => Promise<unknown>) => {
      setBusyId(id);
      setError(null);
      setSuccess(null);
      try {
        await fn();
        await fetchAll();
      } catch (err) {
        setError(err instanceof Error ? err.message : t('settings.error.action'));
      } finally {
        setBusyId(null);
      }
    },
    [fetchAll, t],
  );

  const handleInvite = useCallback(async () => {
    if (!inviteEmail.trim()) return;
    setInviteBusy(true);
    setError(null);
    setSuccess(null);
    try {
      await inviteMember(inviteEmail.trim(), inviteRole);
      setShowInvite(false);
      setInviteEmail('');
      setInviteRole('member');
      setSuccess(t('settings.users.inviteSuccess'));
      fetchAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('settings.users.inviteError'));
    } finally {
      setInviteBusy(false);
    }
  }, [inviteEmail, inviteRole, fetchAll, t]);

  const handleReset = useCallback(
    async (m: TeamMember) => {
      if (!window.confirm(t('settings.users.resetConfirm', { email: m.email }))) return;
      setBusyId(m.id);
      setError(null);
      setSuccess(null);
      try {
        const res = await resetMemberPassword(m.id);
        setResetResult({ email: m.email, password: res.temporary_password });
      } catch (err) {
        setError(err instanceof Error ? err.message : t('settings.error.action'));
      } finally {
        setBusyId(null);
      }
    },
    [t],
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-brand-600">{t('settings.users.subtitle')}</p>
        <button type="button" onClick={() => setShowInvite(true)} className={btnPrimary}>
          {t('settings.users.inviteButton')}
        </button>
      </div>

      {error && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
      {success && <p className="rounded-md bg-green-50 px-3 py-2 text-sm text-green-700">{success}</p>}

      {loading ? (
        <p className="text-sm text-brand-600">{t('settings.loading')}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-brand-200 text-brand-700">
              <tr>
                <th className="py-2 pr-4">{t('settings.users.col.email')}</th>
                <th className="py-2 pr-4">{t('settings.users.col.role')}</th>
                <th className="py-2 pr-4">{t('settings.users.col.status')}</th>
                <th className="py-2 pr-4">{t('settings.users.col.joined')}</th>
                <th className="py-2 pr-4">{t('settings.users.col.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.id} className="border-b border-brand-100">
                  <td className="py-2 pr-4 text-brand-800">{m.email}</td>
                  <td className="py-2 pr-4">
                    <select
                      value={m.role === 'super_user' || m.role === 'owner' ? 'admin' : m.role}
                      onChange={(e) =>
                        run(m.id, () => updateMemberRole(m.id, e.target.value as MemberRole))
                      }
                      disabled={busyId === m.id}
                      className="rounded-md border border-brand-300 px-2 py-1 text-sm text-brand-800 disabled:opacity-50"
                    >
                      {ROLE_OPTIONS.map((r) => (
                        <option key={r} value={r}>
                          {roleLabel(r, t)}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="py-2 pr-4">
                    <span
                      className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-semibold ${
                        m.is_active !== false
                          ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-700'
                      }`}
                    >
                      {m.is_active !== false
                        ? t('settings.users.active')
                        : t('settings.users.disabled')}
                    </span>
                  </td>
                  <td className="py-2 pr-4 text-xs text-brand-500">{formatDate(m.created_at)}</td>
                  <td className="py-2 pr-4">
                    <div className="flex flex-wrap gap-2">
                      {m.is_active !== false ? (
                        <button
                          type="button"
                          onClick={() => run(m.id, () => deactivateMember(m.id))}
                          disabled={busyId === m.id}
                          className={btnDanger}
                        >
                          {t('settings.users.deactivate')}
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => run(m.id, () => activateMember(m.id))}
                          disabled={busyId === m.id}
                          className={btnNeutral}
                        >
                          {t('settings.users.activate')}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => handleReset(m)}
                        disabled={busyId === m.id}
                        className={btnNeutral}
                      >
                        {t('settings.users.resetPassword')}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (window.confirm(t('settings.users.removeConfirm', { email: m.email }))) {
                            run(m.id, () => removeMember(m.id));
                          }
                        }}
                        disabled={busyId === m.id}
                        className={btnDanger}
                      >
                        {t('settings.users.remove')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Convites pendentes */}
      {invites.length > 0 && (
        <div>
          <h3 className="mb-2 text-sm font-semibold text-brand-800">
            {t('settings.users.pendingInvites')}
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <tbody>
                {invites.map((inv) => (
                  <tr key={inv.id} className="border-b border-brand-100">
                    <td className="py-2 pr-4 text-brand-600">{inv.email}</td>
                    <td className="py-2 pr-4 text-brand-800">{roleLabel(inv.role, t)}</td>
                    <td className="py-2 pr-4 text-xs text-brand-500">{formatDate(inv.expires_at)}</td>
                    <td className="py-2 pr-4">
                      <button
                        type="button"
                        onClick={() => run(inv.id, () => resendInvite(inv.id))}
                        className={btnNeutral}
                      >
                        {t('settings.users.resend')}
                      </button>
                      <button
                        type="button"
                        onClick={() => run(inv.id, () => cancelInvite(inv.id))}
                        className={`ml-2 ${btnDanger}`}
                      >
                        {t('settings.users.cancelInvite')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal de convite */}
      {showInvite && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h2 className="mb-4 text-lg font-semibold text-brand-900">
              {t('settings.users.inviteTitle')}
            </h2>
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-xs text-brand-500">
                  {t('settings.users.col.email')}
                </label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="nome@empresa.com"
                  className={inputCls}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-brand-500">
                  {t('settings.users.col.role')}
                </label>
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value as MemberRole)}
                  className={inputCls}
                >
                  {ROLE_OPTIONS.map((r) => (
                    <option key={r} value={r}>
                      {roleLabel(r, t)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowInvite(false)}
                className="rounded-md border border-brand-300 px-4 py-2 text-sm font-medium text-brand-700 hover:bg-brand-50"
              >
                {t('settings.cancel')}
              </button>
              <button
                type="button"
                onClick={handleInvite}
                disabled={inviteBusy || !inviteEmail.trim()}
                className={btnPrimary}
              >
                {inviteBusy ? t('settings.saving') : t('settings.users.sendInvite')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de senha temporaria */}
      {resetResult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h2 className="mb-2 text-lg font-semibold text-brand-900">
              {t('settings.users.resetTitle')}
            </h2>
            <p className="mb-4 text-sm text-brand-600">
              {t('settings.users.resetBody', { email: resetResult.email })}
            </p>
            <p className="mb-4 select-all rounded-md bg-brand-50 px-3 py-2 font-mono text-sm text-brand-900">
              {resetResult.password}
            </p>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setResetResult(null)}
                className={btnPrimary}
              >
                {t('settings.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Provedores (catalogo municipio -> provedor NFS-e)
// ---------------------------------------------------------------------------

// Issue #551: o catalogo e GLOBAL — escrita (CRUD em /api/municipios) e
// restrita a super_user (CATALOG_ADMIN_ROLES; decisao do product owner:
// so a plataforma edita o catalogo global). Parceiro e org admins
// (admin/owner/member) nao editam; contribuem via sugestoes
// (POST /api/catalogo/sugestoes, mesmo fluxo da pagina
// CatalogoMunicipios — onde parceiro CONTINUA moderador de sugestoes,
// spec #516). Fail-closed: se a role nao resolver, mostra a
// visao de sugestao (nunca o CRUD).
const PROVIDER_MODERATION_ROLES = ['super_user'];

function ProvidersTab() {
  const t = useT();
  const [role, setRole] = useState<string | null>(null);

  useEffect(() => {
    fetchMe()
      .then((u) => setRole(u.role))
      .catch(() => setRole(''));
  }, []);

  if (role === null) {
    return <p className="text-sm text-brand-600">{t('settings.loading')}</p>;
  }
  return PROVIDER_MODERATION_ROLES.includes(role) ? (
    <ProvidersCatalogTab />
  ) : (
    <ProvidersSuggestTab />
  );
}

// Visao de moderador: CRUD direto no catalogo global (inalterado).
function ProvidersCatalogTab() {
  const t = useT();
  const [items, setItems] = useState<MunicipioItem[]>([]);
  const [busca, setBusca] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const emptyForm = { codigo_ibge: '', nome: '', uf: '', provedor: '', agregador: '' };
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState<string | null>(null); // codigo_ibge em edicao
  const [showForm, setShowForm] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listMunicipios(busca ? { busca } : undefined);
      setItems(res ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('settings.error.load'));
    } finally {
      setLoading(false);
    }
  }, [busca, t]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const startEdit = (m: MunicipioItem) => {
    setEditing(m.codigo_ibge);
    setForm({
      codigo_ibge: m.codigo_ibge,
      nome: m.nome,
      uf: m.uf,
      provedor: m.provedor,
      agregador: m.agregador ?? '',
    });
    setShowForm(true);
  };

  const handleSave = useCallback(async () => {
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const payload = {
        nome: form.nome.trim(),
        uf: form.uf.trim().toUpperCase(),
        provedor: form.provedor.trim(),
        agregador: form.agregador.trim() || null,
      };
      if (editing) {
        await updateMunicipio(editing, payload);
      } else {
        await createMunicipio({ codigo_ibge: form.codigo_ibge.trim(), ...payload });
      }
      setShowForm(false);
      setEditing(null);
      setForm(emptyForm);
      setSuccess(t('settings.providers.saved'));
      fetchAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('settings.error.action'));
    } finally {
      setBusy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, editing, fetchAll, t]);

  const handleDelete = useCallback(
    async (m: MunicipioItem) => {
      if (!window.confirm(t('settings.providers.deleteConfirm', { name: m.nome }))) return;
      setError(null);
      setSuccess(null);
      try {
        await deleteMunicipio(m.codigo_ibge);
        setSuccess(t('settings.providers.deleted'));
        fetchAll();
      } catch (err) {
        setError(err instanceof Error ? err.message : t('settings.error.action'));
      }
    },
    [fetchAll, t],
  );

  const canSave =
    form.nome.trim() &&
    form.uf.trim().length === 2 &&
    form.provedor.trim() &&
    (editing !== null || form.codigo_ibge.trim().length === 7);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-brand-600">{t('settings.providers.subtitle')}</p>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder={t('settings.providers.searchPlaceholder')}
            className="rounded-md border border-brand-300 px-3 py-2 text-sm text-brand-800"
          />
          <button
            type="button"
            onClick={() => {
              setEditing(null);
              setForm(emptyForm);
              setShowForm(true);
            }}
            className={btnPrimary}
          >
            {t('settings.providers.addButton')}
          </button>
        </div>
      </div>

      {error && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
      {success && <p className="rounded-md bg-green-50 px-3 py-2 text-sm text-green-700">{success}</p>}

      {loading ? (
        <p className="text-sm text-brand-600">{t('settings.loading')}</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-brand-500">{t('settings.providers.empty')}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-brand-200 text-brand-700">
              <tr>
                <th className="py-2 pr-4">{t('settings.providers.col.ibge')}</th>
                <th className="py-2 pr-4">{t('settings.providers.col.name')}</th>
                <th className="py-2 pr-4">{t('settings.providers.col.uf')}</th>
                <th className="py-2 pr-4">{t('settings.providers.col.provider')}</th>
                <th className="py-2 pr-4">{t('settings.providers.col.aggregator')}</th>
                <th className="py-2 pr-4">{t('settings.users.col.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((m) => (
                <tr key={m.codigo_ibge} className="border-b border-brand-100">
                  <td className="py-2 pr-4 text-brand-600">{m.codigo_ibge}</td>
                  <td className="py-2 pr-4 text-brand-800">{m.nome}</td>
                  <td className="py-2 pr-4 text-brand-600">{m.uf}</td>
                  <td className="py-2 pr-4 text-brand-800">{m.provedor}</td>
                  <td className="py-2 pr-4 text-brand-600">{m.agregador ?? '—'}</td>
                  <td className="py-2 pr-4">
                    <button type="button" onClick={() => startEdit(m)} className={btnNeutral}>
                      {t('settings.edit')}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(m)}
                      className={`ml-2 ${btnDanger}`}
                    >
                      {t('settings.delete')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal criar/editar provedor */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h2 className="mb-4 text-lg font-semibold text-brand-900">
              {editing ? t('settings.providers.editTitle') : t('settings.providers.addTitle')}
            </h2>
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-xs text-brand-500">
                  {t('settings.providers.col.ibge')}
                </label>
                <input
                  type="text"
                  value={form.codigo_ibge}
                  onChange={(e) => setForm({ ...form, codigo_ibge: e.target.value })}
                  disabled={editing !== null}
                  maxLength={7}
                  className={`${inputCls} disabled:bg-brand-50`}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-brand-500">
                  {t('settings.providers.col.name')}
                </label>
                <input
                  type="text"
                  value={form.nome}
                  onChange={(e) => setForm({ ...form, nome: e.target.value })}
                  className={inputCls}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-brand-500">
                  {t('settings.providers.col.uf')}
                </label>
                <input
                  type="text"
                  value={form.uf}
                  onChange={(e) => setForm({ ...form, uf: e.target.value.toUpperCase() })}
                  maxLength={2}
                  className={inputCls}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-brand-500">
                  {t('settings.providers.col.provider')}
                </label>
                <input
                  type="text"
                  value={form.provedor}
                  onChange={(e) => setForm({ ...form, provedor: e.target.value })}
                  placeholder="GISS, GINFES, ABRASF..."
                  className={inputCls}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-brand-500">
                  {t('settings.providers.col.aggregator')}
                </label>
                <input
                  type="text"
                  value={form.agregador}
                  onChange={(e) => setForm({ ...form, agregador: e.target.value })}
                  placeholder="PlugNotas, TecnoSpeed, NFe.io..."
                  className={inputCls}
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditing(null);
                }}
                className="rounded-md border border-brand-300 px-4 py-2 text-sm font-medium text-brand-700 hover:bg-brand-50"
              >
                {t('settings.cancel')}
              </button>
              <button type="button" onClick={handleSave} disabled={busy || !canSave} className={btnPrimary}>
                {busy ? t('settings.saving') : t('settings.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Visao de org admin/owner/member (issue #547): sem a lista do catalogo
// global — apenas (a) formulario "Solicitar provedor/municipio" e
// (b) as sugestoes da propria org com status de moderacao.
function ProvidersSuggestTab() {
  const t = useT();
  const [sugestoes, setSugestoes] = useState<CatalogoSugestao[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const emptyForm = {
    codigo_ibge: '',
    nome: '',
    uf: '',
    provedor: '',
    agregador: '',
    justificativa: '',
  };
  const [form, setForm] = useState(emptyForm);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Nao-moderador: o backend ja filtra para as sugestoes da propria org.
      setSugestoes((await fetchCatalogoSugestoes()) ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('settings.error.load'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleSubmit = useCallback(async () => {
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      await createCatalogoSugestao({
        codigo_ibge: form.codigo_ibge.trim(),
        nome: form.nome.trim(),
        uf: form.uf.trim().toUpperCase(),
        provedor: form.provedor.trim(),
        agregador: form.agregador.trim() || null,
        justificativa: form.justificativa.trim() || null,
      });
      setForm(emptyForm);
      setSuccess(t('catalogo.suggest.success'));
      fetchAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('settings.error.action'));
    } finally {
      setBusy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, fetchAll, t]);

  const canSubmit =
    form.codigo_ibge.trim().length === 7 &&
    form.nome.trim() &&
    form.uf.trim().length === 2 &&
    form.provedor.trim();

  return (
    <div className="space-y-4">
      <p className="text-sm text-brand-600">{t('settings.providers.globalNotice')}</p>

      {error && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
      {success && <p className="rounded-md bg-green-50 px-3 py-2 text-sm text-green-700">{success}</p>}

      {/* Formulario de sugestao */}
      <div className="rounded-md border border-brand-200 p-4">
        <h3 className="mb-3 text-sm font-semibold text-brand-800">
          {t('settings.providers.suggestTitle')}
        </h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div>
            <label className="mb-1 block text-xs text-brand-500">
              {t('catalogo.suggest.ibge')}
            </label>
            <input
              type="text"
              value={form.codigo_ibge}
              onChange={(e) => setForm({ ...form, codigo_ibge: e.target.value })}
              maxLength={7}
              className={inputCls}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-brand-500">
              {t('catalogo.suggest.nome')}
            </label>
            <input
              type="text"
              value={form.nome}
              onChange={(e) => setForm({ ...form, nome: e.target.value })}
              className={inputCls}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-brand-500">
              {t('catalogo.suggest.uf')}
            </label>
            <input
              type="text"
              value={form.uf}
              onChange={(e) => setForm({ ...form, uf: e.target.value.toUpperCase() })}
              maxLength={2}
              className={inputCls}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-brand-500">
              {t('catalogo.suggest.provedor')}
            </label>
            <input
              type="text"
              value={form.provedor}
              onChange={(e) => setForm({ ...form, provedor: e.target.value })}
              placeholder="GISS, GINFES, ABRASF..."
              className={inputCls}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-brand-500">
              {t('catalogo.suggest.agregador')}
            </label>
            <input
              type="text"
              value={form.agregador}
              onChange={(e) => setForm({ ...form, agregador: e.target.value })}
              placeholder="PlugNotas, TecnoSpeed, NFe.io..."
              className={inputCls}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-brand-500">
              {t('catalogo.suggest.justificativa')}
            </label>
            <input
              type="text"
              value={form.justificativa}
              onChange={(e) => setForm({ ...form, justificativa: e.target.value })}
              className={inputCls}
            />
          </div>
        </div>
        <div className="mt-4">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={busy || !canSubmit}
            className={btnPrimary}
          >
            {busy ? t('settings.saving') : t('catalogo.suggest.submit')}
          </button>
        </div>
      </div>

      {/* Sugestoes da propria org */}
      <div>
        <h3 className="mb-2 text-sm font-semibold text-brand-800">
          {t('settings.providers.mySuggestions')}
        </h3>
        {loading ? (
          <p className="text-sm text-brand-600">{t('settings.loading')}</p>
        ) : sugestoes.length === 0 ? (
          <p className="text-sm text-brand-500">{t('catalogo.sugestoes.empty')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-brand-200 text-brand-700">
                <tr>
                  <th className="py-2 pr-4">{t('settings.providers.col.ibge')}</th>
                  <th className="py-2 pr-4">{t('settings.providers.col.name')}</th>
                  <th className="py-2 pr-4">{t('settings.providers.col.uf')}</th>
                  <th className="py-2 pr-4">{t('settings.providers.col.provider')}</th>
                  <th className="py-2 pr-4">{t('common.status')}</th>
                </tr>
              </thead>
              <tbody>
                {sugestoes.map((s) => (
                  <tr key={s.id} className="border-b border-brand-100">
                    <td className="py-2 pr-4 text-brand-600">{s.codigo_ibge}</td>
                    <td className="py-2 pr-4 text-brand-800">{s.nome}</td>
                    <td className="py-2 pr-4 text-brand-600">{s.uf}</td>
                    <td className="py-2 pr-4 text-brand-800">{s.provedor}</td>
                    <td className="py-2 pr-4">
                      <span className="text-brand-800">
                        {t(`catalogo.sugestoes.status.${s.status}`)}
                      </span>
                      {s.review_note && (
                        <span className="block text-xs text-brand-500">{s.review_note}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: E-mail (IMAP por org + SMTP por org)
// ---------------------------------------------------------------------------

function EmailTab({ userRole }: { userRole: string }) {
  const t = useT();
  // Issue #572: bloco IMAP e legacy/suporte — visivel so para super_user
  // (diagnostico). Roles de org usam a captura dedicada que o substitui.
  const canSeeImap = userRole === 'super_user';
  const [configs, setConfigs] = useState<EmailConfigItem[]>([]);
  const [settings, setSettings] = useState<OrgSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [testResult, setTestResult] = useState<Record<string, string>>({});

  // Issue #542: accept_any_sender (modo aberto explicito) + allowed_senders
  // (whitelist) expostos no modal — a whitelist so tem efeito quando
  // accept_any_sender=false. O texto da lista vira string[] no save.
  const emptyImap = {
    host: '',
    port: 993,
    username: '',
    password: '',
    folder: 'INBOX',
    accept_any_sender: false,
    allowed_senders_text: '',
  };
  const [imapForm, setImapForm] = useState(emptyImap);
  const [showImapForm, setShowImapForm] = useState(false);
  const [editingImap, setEditingImap] = useState<EmailConfigItem | null>(null);

  const [smtpForm, setSmtpForm] = useState({
    host: '',
    port: 587,
    username: '',
    password: '',
    from_address: '',
    use_tls: true,
    enabled: false,
  });

  // Issue #555: endereco de captura dedicada da org. O GET e lazy-create —
  // a primeira abertura da aba ja provisiona. A pagina inteira e guardada
  // por MANAGE_ROLES (App.tsx), entao o GET/regenerate nao precisam de
  // gate extra aqui (o backend revalida a role de qualquer forma).
  const [capture, setCapture] = useState<CaptureAddressItem | null>(null);
  const [captureCopied, setCaptureCopied] = useState(false);
  // Issue #570: input do novo remetente extra da allowlist de captura.
  const [newCaptureSender, setNewCaptureSender] = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Issue #555: a captura dedicada falha em silencio — um erro no
      // endpoint novo nao pode derrubar as secoes IMAP/SMTP existentes.
      // Issue #572: orgs nao veem o bloco IMAP — o fetch e pulado para
      // evitar chamada desnecessaria (configs permanece []).
      const [c, s, cap] = await Promise.all([
        canSeeImap
          ? listEmailConfigs()
          : Promise.resolve<{ configs: EmailConfigItem[] }>({ configs: [] }),
        getOrgSettings(),
        getCaptureAddress().catch(() => null),
      ]);
      setConfigs(c.configs ?? []);
      setSettings(s);
      setCapture(cap);
      setSmtpForm({
        host: s.smtp.host ?? '',
        port: s.smtp.port,
        username: s.smtp.username ?? '',
        password: '',
        from_address: s.smtp.from_address ?? '',
        use_tls: s.smtp.use_tls,
        enabled: s.smtp.enabled,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('settings.error.load'));
    } finally {
      setLoading(false);
    }
  }, [t, canSeeImap]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleSaveImap = useCallback(async () => {
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      // Issue #542: textarea -> string[] (uma entrada por linha; virgula e
      // ponto-e-virgula tambem separam). Lista vazia + accept_any_sender
      // =false rejeita todos os remetentes (fail-closed no backend).
      const senders = imapForm.allowed_senders_text
        .split(/[\n,;]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      if (editingImap) {
        const payload: Record<string, unknown> = {
          host: imapForm.host.trim(),
          port: imapForm.port,
          username: imapForm.username.trim(),
          folder: imapForm.folder.trim() || 'INBOX',
          allowed_senders: senders,
          accept_any_sender: imapForm.accept_any_sender,
        };
        if (imapForm.password) payload.password = imapForm.password;
        await updateEmailConfig(editingImap.id, payload);
      } else {
        await createEmailConfig({
          host: imapForm.host.trim(),
          port: imapForm.port,
          username: imapForm.username.trim(),
          password: imapForm.password,
          folder: imapForm.folder.trim() || 'INBOX',
          allowed_senders: senders,
          accept_any_sender: imapForm.accept_any_sender,
        });
      }
      setShowImapForm(false);
      setEditingImap(null);
      setImapForm(emptyImap);
      setSuccess(t('settings.email.saved'));
      fetchAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('settings.error.action'));
    } finally {
      setBusy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imapForm, editingImap, fetchAll, t]);

  const handleDeleteImap = useCallback(
    async (c: EmailConfigItem) => {
      if (!window.confirm(t('settings.email.deleteConfirm', { host: c.host }))) return;
      setError(null);
      setSuccess(null);
      try {
        await deleteEmailConfig(c.id);
        setSuccess(t('settings.email.deleted'));
        fetchAll();
      } catch (err) {
        setError(err instanceof Error ? err.message : t('settings.error.action'));
      }
    },
    [fetchAll, t],
  );

  const handleTest = useCallback(
    async (c: EmailConfigItem) => {
      setTestResult((prev) => ({ ...prev, [c.id]: t('settings.email.testing') }));
      try {
        const res = await testEmailConfig(c.id);
        setTestResult((prev) => ({
          ...prev,
          [c.id]: res.success ? `OK: ${res.message}` : `Erro: ${res.error}`,
        }));
      } catch (err) {
        setTestResult((prev) => ({
          ...prev,
          [c.id]: err instanceof Error ? err.message : t('settings.error.action'),
        }));
      }
    },
    [t],
  );

  // Issue #555 — captura dedicada: copiar e regenerar o endereco.
  const handleCopyCapture = useCallback(async () => {
    if (!capture?.capture_address) return;
    try {
      await navigator.clipboard.writeText(capture.capture_address);
      setCaptureCopied(true);
      setTimeout(() => setCaptureCopied(false), 2000);
    } catch {
      setError(t('settings.email.captureCopyError'));
    }
  }, [capture, t]);

  const handleRegenerateCapture = useCallback(async () => {
    if (!capture?.capture_address) return;
    // Confirmacao explicita: o endereco anterior para de rotear — e-mails
    // enviados a ele caem na quarentena de alias desconhecido.
    if (
      !window.confirm(
        t('settings.email.captureRegenerateConfirm', {
          address: capture.capture_address,
        }),
      )
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await regenerateCaptureAddress();
      setCapture(res);
      setSuccess(t('settings.email.captureRegenerated'));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('settings.error.action'));
    } finally {
      setBusy(false);
    }
  }, [capture, t]);

  // Issue #570 — allowlist da captura dedicada: adicionar/remover extras.
  // O backend valida formato estrito (somente e-mail individual; dominios
  // e wildcards -> 422, duplicado -> 409) — a UI so normaliza e exibe o
  // erro retornado.
  const handleAddCaptureSender = useCallback(async () => {
    const email = newCaptureSender.trim();
    if (!email) return;
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await addCaptureSender(email);
      setCapture(res);
      setNewCaptureSender('');
      setSuccess(t('settings.email.captureSenderAdded'));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('settings.error.action'));
    } finally {
      setBusy(false);
    }
  }, [newCaptureSender, t]);

  const handleRemoveCaptureSender = useCallback(
    async (email: string) => {
      setBusy(true);
      setError(null);
      setSuccess(null);
      try {
        const res = await removeCaptureSender(email);
        setCapture(res);
        setSuccess(t('settings.email.captureSenderRemoved'));
      } catch (err) {
        setError(err instanceof Error ? err.message : t('settings.error.action'));
      } finally {
        setBusy(false);
      }
    },
    [t],
  );

  const handleSaveSmtp = useCallback(async () => {
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const smtp: Record<string, unknown> = {
        host: smtpForm.host.trim() || null,
        port: smtpForm.port,
        username: smtpForm.username.trim() || null,
        from_address: smtpForm.from_address.trim() || null,
        use_tls: smtpForm.use_tls,
        enabled: smtpForm.enabled,
      };
      if (smtpForm.password) smtp.password = smtpForm.password;
      const res = await updateOrgSettings({ smtp });
      setSettings(res);
      setSmtpForm((prev) => ({ ...prev, password: '' }));
      setSuccess(t('settings.email.smtpSaved'));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('settings.error.action'));
    } finally {
      setBusy(false);
    }
  }, [smtpForm, t]);

  const canSaveImap =
    imapForm.host.trim() &&
    imapForm.username.trim() &&
    (editingImap !== null || imapForm.password.length > 0);

  return (
    <div className="space-y-6">
      {error && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
      {success && <p className="rounded-md bg-green-50 px-3 py-2 text-sm text-green-700">{success}</p>}

      {/* Issue #555 — Secao "Captura por e-mail" (endereco dedicado) */}
      {capture?.capture_address && (
        <div className="rounded-md border border-brand-200 bg-brand-50/50 p-4">
          <h3 className="mb-1 text-sm font-semibold text-brand-800">
            {t('settings.email.captureTitle')}
          </h3>
          <p className="mb-3 text-xs text-brand-500">
            {t('settings.email.captureSubtitle')}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <code className="rounded border border-brand-200 bg-white px-3 py-1.5 text-sm text-brand-800">
              {capture.capture_address}
            </code>
            <button type="button" onClick={handleCopyCapture} className={btnNeutral}>
              {captureCopied
                ? t('settings.email.captureCopied')
                : t('settings.email.captureCopy')}
            </button>
            <button
              type="button"
              onClick={handleRegenerateCapture}
              disabled={busy}
              className={btnNeutral}
            >
              {t('settings.email.captureRegenerate')}
            </button>
          </div>
          <p className="mt-2 text-xs text-brand-500">
            {t('settings.email.captureRegenerateHint')}
          </p>

          {/* Issue #570 — allowlist de remetentes da captura dedicada.
              Membros ativos da org sao sempre permitidos (o backend nao
              expoe a lista de e-mails deles — privacidade); aqui o admin
              gerencia os remetentes EXTRAS. Fora da lista = recusado
              sem processamento (sem quarentena). */}
          <div className="mt-4 border-t border-brand-200 pt-3">
            <p className="text-xs font-semibold text-brand-700">
              {t('settings.email.captureSendersTitle')}
            </p>
            <p className="mb-2 mt-1 text-xs text-brand-500">
              {t('settings.email.captureSendersHint')}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="email"
                value={newCaptureSender}
                onChange={(e) => setNewCaptureSender(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddCaptureSender();
                  }
                }}
                placeholder={t('settings.email.captureSenderPlaceholder')}
                className="w-64 rounded-md border border-brand-300 px-3 py-1.5 text-sm text-brand-800"
              />
              <button
                type="button"
                onClick={handleAddCaptureSender}
                disabled={busy || !newCaptureSender.trim()}
                className={btnNeutral}
              >
                {t('settings.email.captureSenderAdd')}
              </button>
            </div>
            {(capture.allowed_senders_extra ?? []).length > 0 && (
              <ul className="mt-2 space-y-1">
                {(capture.allowed_senders_extra ?? []).map((sender) => (
                  <li
                    key={sender}
                    className="flex flex-wrap items-center gap-2 text-sm"
                  >
                    <code className="rounded border border-brand-100 bg-white px-2 py-0.5 text-xs text-brand-800">
                      {sender}
                    </code>
                    <button
                      type="button"
                      onClick={() => handleRemoveCaptureSender(sender)}
                      disabled={busy}
                      className={btnDanger}
                    >
                      {t('settings.email.captureSenderRemove')}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <p className="mt-2 text-xs text-brand-500">
              {t('settings.email.captureSendersRejectNote')}
            </p>
          </div>
        </div>
      )}

      {/* Secao IMAP (captura) — Issue #572: legacy/suporte; nao renderiza
          para roles de org (a captura dedicada a substitui). */}
      {canSeeImap && (
      <div>
        <div className="mb-2 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-brand-800">{t('settings.email.imapTitle')}</h3>
            <p className="text-xs text-brand-500">{t('settings.email.imapSubtitle')}</p>
          </div>
          <button
            type="button"
            onClick={() => {
              setEditingImap(null);
              setImapForm(emptyImap);
              setShowImapForm(true);
            }}
            className={btnPrimary}
          >
            {t('settings.email.addImap')}
          </button>
        </div>

        {loading ? (
          <p className="text-sm text-brand-600">{t('settings.loading')}</p>
        ) : configs.length === 0 ? (
          <p className="text-sm text-brand-500">{t('settings.email.empty')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-brand-200 text-brand-700">
                <tr>
                  <th className="py-2 pr-4">{t('settings.email.col.host')}</th>
                  <th className="py-2 pr-4">{t('settings.email.col.user')}</th>
                  <th className="py-2 pr-4">{t('settings.email.col.folder')}</th>
                  <th className="py-2 pr-4">{t('settings.users.col.status')}</th>
                  <th className="py-2 pr-4">{t('settings.users.col.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {configs.map((c) => (
                  <tr key={c.id} className="border-b border-brand-100">
                    <td className="py-2 pr-4 text-brand-800">
                      {c.host}:{c.port}
                    </td>
                    <td className="py-2 pr-4 text-brand-600">{c.username}</td>
                    <td className="py-2 pr-4 text-brand-600">{c.folder}</td>
                    <td className="py-2 pr-4">
                      <span
                        className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-semibold ${
                          c.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {c.enabled ? t('settings.users.active') : t('settings.users.disabled')}
                      </span>
                    </td>
                    <td className="py-2 pr-4">
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleTest(c)}
                          className={btnNeutral}
                        >
                          {t('settings.email.test')}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setEditingImap(c);
                            setImapForm({
                              host: c.host,
                              port: c.port,
                              username: c.username,
                              password: '',
                              folder: c.folder,
                              accept_any_sender: c.accept_any_sender,
                              allowed_senders_text: (c.allowed_senders ?? []).join('\n'),
                            });
                            setShowImapForm(true);
                          }}
                          className={btnNeutral}
                        >
                          {t('settings.edit')}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteImap(c)}
                          className={btnDanger}
                        >
                          {t('settings.delete')}
                        </button>
                      </div>
                      {testResult[c.id] && (
                        <p className="mt-1 text-xs text-brand-500">{testResult[c.id]}</p>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}

      {/* Secao SMTP (envio) */}
      <div className="border-t border-brand-100 pt-4">
        <h3 className="mb-1 text-sm font-semibold text-brand-800">{t('settings.email.smtpTitle')}</h3>
        <p className="mb-3 text-xs text-brand-500">{t('settings.email.smtpSubtitle')}</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs text-brand-500">{t('settings.email.col.host')}</label>
            <input
              type="text"
              value={smtpForm.host}
              onChange={(e) => setSmtpForm({ ...smtpForm, host: e.target.value })}
              placeholder="smtp.exemplo.com"
              className={inputCls}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-brand-500">{t('settings.email.col.port')}</label>
            <input
              type="number"
              value={smtpForm.port}
              onChange={(e) => setSmtpForm({ ...smtpForm, port: Number(e.target.value) })}
              className={inputCls}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-brand-500">{t('settings.email.col.user')}</label>
            <input
              type="text"
              value={smtpForm.username}
              onChange={(e) => setSmtpForm({ ...smtpForm, username: e.target.value })}
              className={inputCls}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-brand-500">
              {t('settings.email.col.password')}
              {settings?.smtp.password_set ? ` (${t('settings.email.passwordSet')})` : ''}
            </label>
            <input
              type="password"
              value={smtpForm.password}
              onChange={(e) => setSmtpForm({ ...smtpForm, password: e.target.value })}
              placeholder={settings?.smtp.password_set ? '••••••••' : ''}
              className={inputCls}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-brand-500">
              {t('settings.email.col.from')}
            </label>
            <input
              type="email"
              value={smtpForm.from_address}
              onChange={(e) => setSmtpForm({ ...smtpForm, from_address: e.target.value })}
              placeholder="no-reply@empresa.com"
              className={inputCls}
            />
          </div>
          <div className="flex items-end gap-4 pb-1">
            <label className="flex items-center gap-2 text-sm text-brand-700">
              <input
                type="checkbox"
                checked={smtpForm.use_tls}
                onChange={(e) => setSmtpForm({ ...smtpForm, use_tls: e.target.checked })}
                className="h-4 w-4 rounded border-brand-300 text-brand-600"
              />
              {t('settings.email.useTls')}
            </label>
            <label className="flex items-center gap-2 text-sm text-brand-700">
              <input
                type="checkbox"
                checked={smtpForm.enabled}
                onChange={(e) => setSmtpForm({ ...smtpForm, enabled: e.target.checked })}
                className="h-4 w-4 rounded border-brand-300 text-brand-600"
              />
              {t('settings.email.enabled')}
            </label>
          </div>
        </div>
        <div className="mt-3">
          <button type="button" onClick={handleSaveSmtp} disabled={busy} className={btnPrimary}>
            {busy ? t('settings.saving') : t('settings.email.saveSmtp')}
          </button>
        </div>
      </div>

      {/* Modal IMAP — #572: mesmo gate do bloco (so super_user) */}
      {canSeeImap && showImapForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-lg bg-white p-6 shadow-xl">
            <h2 className="mb-4 text-lg font-semibold text-brand-900">
              {editingImap ? t('settings.email.editImap') : t('settings.email.addImap')}
            </h2>
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-xs text-brand-500">
                  {t('settings.email.col.host')}
                </label>
                <input
                  type="text"
                  value={imapForm.host}
                  onChange={(e) => setImapForm({ ...imapForm, host: e.target.value })}
                  placeholder="imap.exemplo.com"
                  className={inputCls}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-brand-500">
                  {t('settings.email.col.port')}
                </label>
                <input
                  type="number"
                  value={imapForm.port}
                  onChange={(e) => setImapForm({ ...imapForm, port: Number(e.target.value) })}
                  className={inputCls}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-brand-500">
                  {t('settings.email.col.user')}
                </label>
                <input
                  type="text"
                  value={imapForm.username}
                  onChange={(e) => setImapForm({ ...imapForm, username: e.target.value })}
                  className={inputCls}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-brand-500">
                  {t('settings.email.col.password')}
                  {editingImap ? ` (${t('settings.email.keepPassword')})` : ''}
                </label>
                <input
                  type="password"
                  value={imapForm.password}
                  onChange={(e) => setImapForm({ ...imapForm, password: e.target.value })}
                  className={inputCls}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-brand-500">
                  {t('settings.email.col.folder')}
                </label>
                <input
                  type="text"
                  value={imapForm.folder}
                  onChange={(e) => setImapForm({ ...imapForm, folder: e.target.value })}
                  className={inputCls}
                />
              </div>
              {/* Issue #542: whitelist de remetentes + opt-out explicito. */}
              <div>
                <label className="mb-1 block text-xs text-brand-500">
                  {t('settings.email.allowedSenders')}
                </label>
                <textarea
                  value={imapForm.allowed_senders_text}
                  onChange={(e) =>
                    setImapForm({ ...imapForm, allowed_senders_text: e.target.value })
                  }
                  placeholder={'nfe@empresa.com\n*@prefeitura.gov.br'}
                  rows={3}
                  disabled={imapForm.accept_any_sender}
                  className={`${inputCls} disabled:bg-brand-50 disabled:text-brand-400`}
                />
                <p className="mt-1 text-xs text-brand-400">
                  {t('settings.email.allowedSendersHint')}
                </p>
                <p className="mt-1 text-xs text-brand-400">
                  {t('settings.email.allowedSendersAuthHint')}
                </p>
              </div>
              <label className="flex items-center gap-2 text-sm text-brand-700">
                <input
                  type="checkbox"
                  checked={imapForm.accept_any_sender}
                  onChange={(e) =>
                    setImapForm({ ...imapForm, accept_any_sender: e.target.checked })
                  }
                  className="h-4 w-4 rounded border-brand-300 text-brand-600"
                />
                {t('settings.email.acceptAnySender')}
              </label>
              {imapForm.accept_any_sender && (
                <p className="rounded-md bg-yellow-50 px-3 py-2 text-xs text-yellow-800">
                  {t('settings.email.acceptAnyWarning')}
                </p>
              )}
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowImapForm(false);
                  setEditingImap(null);
                }}
                className="rounded-md border border-brand-300 px-4 py-2 text-sm font-medium text-brand-700 hover:bg-brand-50"
              >
                {t('settings.cancel')}
              </button>
              <button
                type="button"
                onClick={handleSaveImap}
                disabled={busy || !canSaveImap}
                className={btnPrimary}
              >
                {busy ? t('settings.saving') : t('settings.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Retencao (LGPD)
// ---------------------------------------------------------------------------

function RetentionTab() {
  const t = useT();
  const [days, setDays] = useState<number>(1825);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getOrgSettings()
      .then((s) => setDays(s.retention.retention_days))
      .catch((err) => setError(err instanceof Error ? err.message : t('settings.error.load')))
      .finally(() => setLoading(false));
  }, [t]);

  const handleSave = useCallback(async () => {
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await updateOrgSettings({ retention_days: days });
      setDays(res.retention.retention_days);
      setSuccess(t('settings.retention.saved'));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('settings.error.action'));
    } finally {
      setBusy(false);
    }
  }, [days, t]);

  return (
    <div className="space-y-4">
      <p className="text-sm text-brand-600">{t('settings.retention.subtitle')}</p>
      {error && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
      {success && <p className="rounded-md bg-green-50 px-3 py-2 text-sm text-green-700">{success}</p>}
      {loading ? (
        <p className="text-sm text-brand-600">{t('settings.loading')}</p>
      ) : (
        <div className="max-w-sm">
          <label className="mb-1 block text-xs text-brand-500">
            {t('settings.retention.daysLabel')}
          </label>
          <input
            type="number"
            min={30}
            max={3650}
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className={inputCls}
          />
          <p className="mt-1 text-xs text-brand-400">{t('settings.retention.hint')}</p>
          {/* Issue #560: aviso de que a retenção NÃO apaga documentos —
              vencido o período, admins recebem alerta para revisão. */}
          <p className="mt-2 text-sm text-brand-600">{t('settings.retention.notice')}</p>
          <div className="mt-3">
            <button
              type="button"
              onClick={handleSave}
              disabled={busy || days < 30 || days > 3650}
              className={btnPrimary}
            >
              {busy ? t('settings.saving') : t('settings.save')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Thresholds
// ---------------------------------------------------------------------------

function ThresholdsTab() {
  const t = useT();
  const [values, setValues] = useState({ vlm_confidence: 0.7, auto_approve: 0.9, needs_review: 0.6 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getOrgSettings()
      .then((s) => setValues(s.thresholds))
      .catch((err) => setError(err instanceof Error ? err.message : t('settings.error.load')))
      .finally(() => setLoading(false));
  }, [t]);

  const handleSave = useCallback(async () => {
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await updateOrgSettings({ thresholds: values });
      setValues(res.thresholds);
      setSuccess(t('settings.thresholds.saved'));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('settings.error.action'));
    } finally {
      setBusy(false);
    }
  }, [values, t]);

  const fields: { key: keyof typeof values; label: string; hint: string }[] = [
    {
      key: 'vlm_confidence',
      label: t('settings.thresholds.vlm'),
      hint: t('settings.thresholds.vlmHint'),
    },
    {
      key: 'auto_approve',
      label: t('settings.thresholds.autoApprove'),
      hint: t('settings.thresholds.autoApproveHint'),
    },
    {
      key: 'needs_review',
      label: t('settings.thresholds.needsReview'),
      hint: t('settings.thresholds.needsReviewHint'),
    },
  ];

  const valid = fields.every((f) => values[f.key] >= 0 && values[f.key] <= 1);

  return (
    <div className="space-y-4">
      <p className="text-sm text-brand-600">{t('settings.thresholds.subtitle')}</p>
      {error && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
      {success && <p className="rounded-md bg-green-50 px-3 py-2 text-sm text-green-700">{success}</p>}
      {loading ? (
        <p className="text-sm text-brand-600">{t('settings.loading')}</p>
      ) : (
        <div className="max-w-md space-y-4">
          {fields.map((f) => (
            <div key={f.key}>
              <label className="mb-1 block text-xs text-brand-500">{f.label}</label>
              <input
                type="number"
                min={0}
                max={1}
                step={0.05}
                value={values[f.key]}
                onChange={(e) => setValues({ ...values, [f.key]: Number(e.target.value) })}
                className={inputCls}
              />
              <p className="mt-1 text-xs text-brand-400">{f.hint}</p>
            </div>
          ))}
          <button type="button" onClick={handleSave} disabled={busy || !valid} className={btnPrimary}>
            {busy ? t('settings.saving') : t('settings.save')}
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pagina
// ---------------------------------------------------------------------------

function TenantSettings({ userRole }: { userRole: string }) {
  const t = useT();
  const [tab, setTab] = useState<TabId>('users');

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-brand-200 bg-white p-6 shadow-sm">
        <div className="mb-4">
          <h1 className="text-2xl font-bold text-brand-900">{t('settings.title')}</h1>
          <p className="text-sm text-brand-600">{t('settings.subtitle')}</p>
        </div>

        {/* Tabs */}
        <div className="mb-6 flex gap-1 overflow-x-auto border-b border-brand-200">
          {TABS.map((id) => (
            <button
              key={id}
              type="button"
              onClick={() => setTab(id)}
              className={`whitespace-nowrap px-4 py-2 text-sm font-medium ${
                tab === id
                  ? 'border-b-2 border-brand-700 text-brand-900'
                  : 'text-brand-500 hover:text-brand-800'
              }`}
            >
              {t(`settings.tab.${id}`)}
            </button>
          ))}
        </div>

        {tab === 'users' && <UsersTab />}
        {tab === 'providers' && <ProvidersTab />}
        {tab === 'email' && <EmailTab userRole={userRole} />}
        {tab === 'retention' && <RetentionTab />}
        {tab === 'thresholds' && <ThresholdsTab />}
      </div>
    </div>
  );
}

export default TenantSettings;
